/*
 * landing-page
 * ==================================================================
 * Forked from assets/multi-discount.js (which powers the live lp25x pages — do
 * NOT edit that one). Same behaviour, renamed custom elements so the two coexist:
 *   virtual-cart -> lp-cart   |   bundle-item -> lp-bundle-item   |   upsell-item -> lp-upsell-item
 *
 * Single file (no bundler), organised by concern:
 *   1. Data layer  — lpDiscountApi (Hy global) + thin adapters (getTotals, buildMilestones,
 *                    discountedUnitPrice) over the PURE layer in assets/lp-pricing.js
 *   2. UI widgets  — LpTracker, LpBurst (DOM/styling for the footer)
 *   3. Elements    — LpVariantItem (base) -> LpBundleItem / LpUpsellItem, and LpCart
 *
 * The pure, DOM-free pricing math lives in assets/lp-pricing.js (window.LpPricing),
 * loaded before this file — so it's unit-testable in Node with a fake calculator. This
 * file only reads/writes the DOM and injects the Hy calculator into those pure functions.
 *
 * Preserved: virtual multi-select, discount totals via the external Hy app, add-all
 * with `_lp_exclusive`, clear-then-add, redirect to /checkout, and the data-* paint
 * contract. Fallback: when the Hy global is absent (dev theme), totals sum the line
 * prices (no discount).
 */

/* ════════════════════════ 1. Data layer ════════════════════════ */

// Pure pricing functions (no DOM/globals) — see assets/lp-pricing.js. Loaded first, so
// window.LpPricing is defined by the time this script runs.
const {
  formatMoney,
  selectionCount,
  isEarned,
  cartTotals,
  lineDiscountedPrice,
  parseDiscountConfig,
} = window.LpPricing;

// The live discount global injected by the Hy "zoeva-discounts" app embed.
function lpDiscountApi() {
  return (
    window.Hy &&
    window.Hy.ZoevaDiscounts &&
    window.Hy.ZoevaDiscounts.landingPageExclusiveDiscount
  );
}

// Original/discounted totals (cents). Adapter: injects the Hy app into the pure cartTotals
// (which sums line prices when the app is absent).
function getTotals(cart, basePrice) {
  return cartTotals(cart, basePrice, lpDiscountApi());
}

// Derive the ordered milestone ladder for THIS page's products. Adapter: reads the Hy
// config element + the page's products from the DOM, then hands plain data to the pure
// parseDiscountConfig (which builds the base / scalePrice / 100%-off-gift ladder).
function buildMilestones(host, currency, debug) {
  const api = lpDiscountApi();
  const el = api && api.functionId && document.getElementById(api.functionId);
  if (!el) return [];
  let cfg;
  try {
    cfg = JSON.parse(el.textContent);
  } catch (e) {
    return [];
  }
  if (debug) {
    console.log("[lp-cart] discount config source = zoeva-discounts app", {
      functionId: api.functionId,
      sourceElement: el,
      rawConfig: cfg,
    });
  }

  const items = Array.from(host.querySelectorAll("lp-bundle-item"));
  const pageProducts = new Set(items.map((i) => i.getAttribute("product-id")));
  const prices = items.map((i) => Number(i.getAttribute("variant-price")));
  return parseDiscountConfig(cfg, pageProducts, prices, currency);
}

/* ════════════════════════ 2. UI widgets ════════════════════════ */

// Savings tracker: segmented rail + labels + headline. update() returns
// advancement info so the cart can fire a celebration burst.
class LpTracker {
  constructor(host) {
    this.host = host;
    this.prevCurrentIdx = -1;
    this.prevCount = 0;
    this.prevSaved = null;
    this.armed = false;
    this.reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  init(milestones, format) {
    this.milestones = milestones || [];
    this.format = format;
    this.bar = this.host.querySelector("[data-track-bar]");
    this.fill = this.host.querySelector("[data-track-fill]");
    this.countEl = this.host.querySelector("[data-track-count]");
    this.amtEl = this.host.querySelector("[data-track-amt]");
    this.bestEl = this.host.querySelector("[data-track-best]");
    this.cosEl = this.host.querySelector("[data-track-cos]");
    this.statusEl = this.host.querySelector("[data-track-status]");
    this.trackEl = this.host.querySelector("[data-track]");

    this.overrides = {};
    const ovEl = this.host.querySelector("[data-lp-milestone-labels]");
    if (ovEl) {
      try {
        this.overrides = JSON.parse(ovEl.textContent) || {};
      } catch (e) {}
    }

    this.render();
    this.update(0, 0, 0);
  }

  // Make the progress status a polite live region only AFTER the initial load
  // cascade, so the pre-selected state isn't announced — only later changes.
  arm() {
    this.armed = true;
    if (this.statusEl) {
      this.statusEl.setAttribute("aria-live", "polite");
      this.statusEl.setAttribute("aria-atomic", "true");
    }
  }

  render() {
    if (!this.bar || !this.milestones.length) return;
    // One dot per milestone, spaced evenly along the bar.
    this.bar.querySelectorAll(".lp-track__dot").forEach((d) => d.remove());
    this.dots = [];
    const M = this.milestones.length;
    this.milestones.forEach((m, i) => {
      const dot = document.createElement("span");
      dot.className = "lp-track__dot";
      dot.style.left = (M > 1 ? (i / (M - 1)) * 100 : 0) + "%";
      this.bar.appendChild(dot);
      this.dots.push(dot);
    });
  }

  // Returns { node, idx, advanced } so the cart can fire a burst on a new tier.
  // original/discounted are whole-cart totals (cents) for the savings readout.
  update(count, original, discounted) {
    if (!this.dots || !this.dots.length)
      return { advanced: false, idx: -1, node: null };
    const ms = this.milestones;
    const M = ms.length;
    let currentIdx = -1;
    this.dots.forEach((dot, i) => {
      const done = count >= ms[i].quantity;
      const newlyDone = done && this.prevCount < ms[i].quantity;
      dot.classList.toggle("is-done", done);
      dot.classList.remove("is-current");
      if (done) currentIdx = i;
      if (newlyDone && this.armed && !this.reduceMotion) this.twinkleDot(dot);
    });
    if (currentIdx >= 0) this.dots[currentIdx].classList.add("is-current");

    if (this.fill) {
      this.fill.style.width =
        (currentIdx >= 0 && M > 1 ? (currentIdx / (M - 1)) * 100 : 0) + "%";
    }
    if (this.countEl) this.countEl.textContent = count;

    this.updateSavings(count, original, discounted);
    this.updateCompanions(count);
    this.updateStatus(count);

    const advanced = currentIdx >= 0 && currentIdx > this.prevCurrentIdx;
    this.prevCurrentIdx = currentIdx;
    this.prevCount = count;
    return {
      node: currentIdx >= 0 ? this.dots[currentIdx] : null,
      idx: currentIdx,
      advanced,
    };
  }

  // Pulse a newly-reached dot (scale only). The spark lives on the savings
  // readout now (see sparkSavings), matching lp-demo-5's px2Tw / px2Spark split.
  twinkleDot(dot) {
    dot.classList.remove("is-twinkle");
    void dot.offsetWidth; // restart if rapidly re-triggered
    dot.classList.add("is-twinkle");
    dot.addEventListener(
      "animationend",
      (e) => {
        if (e.animationName === "lpTrackTwinkle")
          dot.classList.remove("is-twinkle");
      },
      { once: true },
    );
  }

  // Burst a single star over the saved-amount <b> — fired only when savings rise
  // (lp-demo-5 px2Spark, pinned inside the relatively-positioned amount).
  sparkSavings() {
    if (!this.amtEl) return;
    const target = this.amtEl.querySelector("b") || this.amtEl;
    const spark = document.createElement("span");
    spark.className = "lp-track__spark";
    spark.setAttribute("aria-hidden", "true");
    spark.addEventListener("animationend", () => spark.remove(), {
      once: true,
    });
    target.appendChild(spark);
  }

  // Right-hand readout: "€X saved (Y%)" + the best-value subline.
  updateSavings(count, original, discounted) {
    const ds = this.trackEl ? this.trackEl.dataset : {};
    if (this.amtEl) {
      const saved = Math.max((original || 0) - (discounted || 0), 0);
      const pct = original > 0 ? Math.round((saved * 100) / original) : 0;
      const savedLabel = ds.savedLabel || "saved";
      this.amtEl.innerHTML =
        "<b>" +
        formatMoney(saved, this.format) +
        "</b> " +
        savedLabel +
        ' <span class="lp-track__pct">(' +
        pct +
        "%)</span>";
      this.amtEl.classList.toggle("is-off", saved <= 0);
      // Spark over the amount only when the saved value actually rises.
      if (
        this.armed &&
        !this.reduceMotion &&
        this.prevSaved !== null &&
        saved > this.prevSaved
      ) {
        this.sparkSavings();
      }
      this.prevSaved = saved;
    }
    if (this.bestEl) {
      const maxQ = this.milestones[this.milestones.length - 1].quantity;
      this.bestEl.textContent =
        count >= maxQ ? ds.complete || "" : ds.bestValue || "";
    }
  }

  // Companion pills for add-ons in the cart: gifts free when their threshold is
  // reached ("Free included", from count), else paid (price) when user-added.
  updateCompanions(count) {
    if (!this.cosEl) return;
    const esc = (s) =>
      String(s).replace(
        /[&<>"]/g,
        (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c],
      );
    const freeText =
      (this.trackEl && this.trackEl.dataset.giftFreeText) || "Free included";
    let html = "";
    this.host.querySelectorAll("lp-upsell-item").forEach((el) => {
      const trigger = Number(el.getAttribute("trigger-quantity")) || 0;
      const name =
        el.getAttribute("data-name") || el.getAttribute("product-title") || "";
      // Prefer the live discounted unit price stamped by LpCart.updateUpsellPrices
      // (e.g. half price once the shade threshold is met); fall back to full price.
      const price =
        typeof el._lpUnitPrice === "number"
          ? el._lpUnitPrice
          : Number(el.getAttribute("variant-price")) || 0;
      const input = el.querySelector("input");
      const free = trigger > 0 && count >= trigger;
      const inCart = input ? input.checked : false;
      if (!free && !inCart) return; // not in the cart → no pill
      const tag = free ? esc(freeText) : formatMoney(price * 100, this.format);
      html +=
        '<span class="lp-track__co lp-track__co--' +
        (free ? "free" : "paid") +
        '">' +
        '<span class="lp-track__co-plus">+</span>' +
        esc(name) +
        ' <i class="lp-track__co-tag">' +
        tag +
        "</i></span>";
    });
    this.cosEl.innerHTML = html;
  }

  // Visually-hidden progress sentence for screen readers (live region after arm()).
  updateStatus(count) {
    if (!this.statusEl) return;
    const ms = this.milestones;
    const ds = this.trackEl ? this.trackEl.dataset : {};
    const maxQ = ms[ms.length - 1].quantity;
    let msg;
    if (count <= 0) {
      msg = ds.empty || "";
    } else {
      const current = ms.filter((m) => count >= m.quantity).pop();
      const message =
        current &&
        this.overrides[current.quantity] &&
        this.overrides[current.quantity].message;
      if (message != null && String(message).trim() !== "") {
        msg = message;
      } else if (count >= maxQ) {
        msg = ds.complete || "";
      } else {
        const next = ms.find((m) => m.quantity > count);
        msg = "Add " + (next.quantity - count) + " more";
      }
    }
    this.statusEl.textContent = msg;
  }
}

// Step celebration particles fired from a tracker node.
class LpBurst {
  constructor(host) {
    this.host = host;
    this.el = host.querySelector("[data-lp-burst]");
    this.style = this.el ? this.el.dataset.style : "none";
  }

  get active() {
    return !!this.el && !!this.style && this.style !== "none";
  }

  fire(node, count) {
    if (!this.active || !node) return;
    const track = this.host.querySelector("[data-track]");
    if (!track) return;
    const tr = track.getBoundingClientRect();
    const nr = node.getBoundingClientRect();
    const x = nr.left - tr.left + nr.width / 2;
    const y = nr.top - tr.top + nr.height / 2;
    const n = Math.min(Math.max(count, 1), 4);
    const TAU = Math.PI * 2;
    const px = (v) => v.toFixed(1) + "px";
    const SPK =
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c1 6.6 4.4 10 11.5 12C16.6 13.6 13 17 12 24c-1-7-4.4-10.4-11.5-12C7.4 10 11 6.6 12 0z"/></svg>';
    const STAR =
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1.6l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.8 5.9 21l1.5-6.8L2.2 8.6l6.9-.7z"/></svg>';
    const PETAL =
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.5 7 5 9 5 13.5A7 7 0 0019 13.5C19 9 15.5 7 12 2z"/></svg>';
    let i, a, R;

    if (this.style === "sparkle") {
      for (i = 0; i < n; i++) {
        a = (i / n) * TAU - 0.5 + Math.random() * 0.3;
        R = 22 + Math.random() * 14;
        this.spawn(
          "scatter",
          x,
          y,
          SPK,
          {
            "--dx": px(Math.cos(a) * R),
            "--dy": px(Math.sin(a) * R),
            "--s": (0.75 + Math.random() * 0.45).toFixed(2),
            "--rot": Math.random() * 120 - 60 + "deg",
          },
          820,
          i * 55,
        );
      }
    } else if (this.style === "twinkle") {
      // n stars (= step reached), jittered around the node, each looping n times.
      for (i = 0; i < n; i++) {
        const jx = (i - (n - 1) / 2) * 16 + (Math.random() * 8 - 4);
        const jy = Math.random() * 26 - 14;
        const el = this.spawn("twk", x + jx, y + jy, STAR, {}, 780, i * 120);
        if (el) el.style.animationIterationCount = String(n);
      }
    } else if (this.style === "pop") {
      this.kickNode(node);
      this.spawn("ripp ring", x, y, "", {}, 620);
      const pr = this.el.lastChild;
      pr.style.width = "46px";
      pr.style.height = "46px";
      pr.style.borderWidth = "2.5px";
      for (i = 0; i < n; i++) {
        a = (i / n) * TAU + 0.4;
        R = 20;
        this.spawn(
          "scatter",
          x,
          y,
          SPK,
          {
            "--dx": px(Math.cos(a) * R),
            "--dy": px(Math.sin(a) * R),
            "--s": "0.7",
            "--rot": "0deg",
          },
          640,
          i * 40,
        );
      }
    } else if (this.style === "bloom") {
      for (i = 0; i < n; i++) {
        a = (i / n) * TAU - 1.57;
        R = 24;
        this.spawn(
          "scatter",
          x,
          y,
          PETAL,
          {
            "--dx": px(Math.cos(a) * R),
            "--dy": px(Math.sin(a) * R),
            "--s": "1",
            "--rot": (a * 180) / Math.PI + 90 + "deg",
          },
          880,
          i * 45,
        );
      }
    } else if (this.style === "rise") {
      for (i = 0; i < n; i++) {
        this.spawn(
          "floaty",
          x,
          y,
          '<span class="dot"></span>',
          {
            "--dx": px((i - (n - 1) / 2) * 13),
          },
          1000,
          i * 120,
        );
      }
    }
  }

  spawn(cls, x, y, html, vars, dur, delay) {
    const el = document.createElement("span");
    el.className = "lp-p " + cls;
    el.style.left = x + "px";
    el.style.top = y + "px";
    if (html != null) el.innerHTML = html;
    if (vars) for (const k in vars) el.style.setProperty(k, vars[k]);
    el.style.animationDuration = (dur || 900) + "ms";
    if (delay) el.style.animationDelay = delay + "ms";
    el.addEventListener("animationend", () => el.remove());
    this.el.appendChild(el);
    return el;
  }

  kickNode(node) {
    if (!node) return;
    node.classList.remove("kick");
    void node.offsetWidth;
    node.classList.add("kick");
    setTimeout(() => node.classList.remove("kick"), 650);
  }
}

/* ════════════════════════ 3. Custom elements ════════════════════════ */

// Base: virtual-cart line bookkeeping shared by bundle + upsell items.
class LpVariantItem extends HTMLElement {
  addItem() {
    const item = this.data;
    const lines = this.cart?.data?.lines;
    if (lines) {
      const existing = lines.find((l) => l.variantId === item.variantId);
      if (existing) existing.quantity += item.quantity;
      else lines.push(item);
    }
    this.dispatchEvent(
      new CustomEvent("lp-cart:change", {
        bubbles: true,
        detail: { item, action: "add", cart: this.cart.data },
      }),
    );
  }

  removeItem() {
    const item = this.data;
    const lines = this.cart?.data?.lines;
    if (lines) {
      const existing = lines.find((l) => l.variantId === item.variantId);
      if (existing && existing.quantity > item.quantity)
        existing.quantity -= item.quantity;
      else
        this.cart.data.lines = lines.filter(
          (l) => l.variantId !== item.variantId,
        );
    }
    this.dispatchEvent(
      new CustomEvent("lp-cart:change", {
        bubbles: true,
        detail: { item, action: "remove", cart: this.cart.data },
      }),
    );
  }

  readData(type) {
    return {
      title: this.getAttribute("product-title") || "",
      type,
      productId: this.hasAttribute("product-id")
        ? Number(this.getAttribute("product-id"))
        : null,
      variantId: this.hasAttribute("variant-id")
        ? Number(this.getAttribute("variant-id"))
        : null,
      price: this.hasAttribute("variant-price")
        ? Number(this.getAttribute("variant-price"))
        : null,
      compareAtPrice: this.hasAttribute("variant-compare-at-price")
        ? Number(this.getAttribute("variant-compare-at-price"))
        : null,
      quantity: 1,
    };
  }

  bindInput(selector) {
    this.input = this.querySelector(selector);
    if (!this.input) return;
    if (this.input.checked) this.addItem();
    this.input.addEventListener("input", () => {
      if (this.input.checked) this.addItem();
      else this.removeItem();
    });
  }
}

// Wire the in-card video. The card is a <label>, so the play button is the ONLY
// video control: it toggles play/pause and swallows its own click (preventDefault +
// stopPropagation) so it never toggles selection. We deliberately put NO handler on
// the <video>/pane — a click there falls through to the <label> and selects the
// card, exactly like the other pane. The button stays visible (icon swaps via the
// host's `is-playing` class) so there's always a control.
function wireCardVideo(host, videoSelector, opts) {
  const video = host.querySelector(videoSelector);
  const play = host.querySelector("[data-card-play]");
  if (!video || !play) return;
  const keepMuted = !!(opts && opts.keepMuted); // upsell plays silently

  play.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (video.paused) {
      if (!keepMuted) video.muted = false;
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      video.pause();
    }
  });
  video.addEventListener("playing", () => {
    host.classList.add("is-playing");
    play.setAttribute("aria-label", "Pause video");
  });
  video.addEventListener("pause", () => {
    host.classList.remove("is-playing");
    play.setAttribute("aria-label", "Play video");
  });
  // Non-looping video (the upsell plays once): when it ends, restore the play control and
  // rewind so a second tap replays from the start. No-op for looping videos (never end).
  video.addEventListener("ended", () => {
    host.classList.remove("is-playing");
    play.setAttribute("aria-label", "Play video");
    video.currentTime = 0;
  });
}

// A selectable shade card; also wires the tap-to-play video in the "eye" pane.
class LpBundleItem extends LpVariantItem {
  connectedCallback() {
    // The finder result layout re-parents cards (moves them between grids), which
    // fires connectedCallback again — only wire once, or the input gets duplicate
    // listeners and the item is re-added to the cart.
    if (this._wired) return;
    this._wired = true;
    this.cart = this.closest("lp-cart");
    this.data = this.readData("default");
    this.bindInput("input.lp-bundle-input");
    this.initCardVideo();
  }

  initCardVideo() {
    wireCardVideo(this, ".lp-card__video");
  }
}

// A gift / paid add-on row with three display states (see lp-product-tile):
//   earned  — shade count >= trigger: free, locked in (the Hy app zeroes it).
//   added   — user added it below the threshold: paid, removable.
//   default — addable.
// Cart membership = earned OR userAdded. `userAdded` is remembered, so a paid add
// flips to free when the threshold is reached, and back to paid if it drops below.
// Add-ons (no trigger) simply toggle default <-> added.
class LpUpsellItem extends LpVariantItem {
  connectedCallback() {
    if (this._wired) return; // guard re-init if ever re-parented (see LpBundleItem)
    this._wired = true;
    this.cart = this.closest("lp-cart");
    this.data = this.readData("upsell");
    this.input = this.querySelector("input.lp-upsell-input");
    if (!this.input) return;

    this.triggerQuantity = this.hasAttribute("trigger-quantity")
      ? Number(this.getAttribute("trigger-quantity"))
      : 0;
    this.isGift = this.triggerQuantity > 0;
    this.userAdded = this.input.checked; // preselected add-ons start added
    this.earned = false;

    if (this.input.checked) this.addItem();

    this.input.addEventListener("input", () => this.onToggle());
    this.initCardVideo();

    if (this.isGift) {
      this.cart.addEventListener("lp-cart:change", (event) => {
        if (event.detail.item.type !== "default") return;
        this.syncEarned();
      });
      this.syncEarned();
    } else {
      this.setState();
    }

    // Paint this tile's price on load. The cart's change-driven paint may have already
    // run for the preselected shades BEFORE this upsell upgraded (tiles sit after the
    // shade grid in the DOM), so its discounted pill was skipped. Re-run now that this
    // element's data exists — idempotent, and covers either upgrade order.
    if (this.cart && typeof this.cart.updateUpsellPrices === "function") {
      this.cart.updateUpsellPrices(this.cart.data);
    }
  }

  initCardVideo() {
    wireCardVideo(this, ".lp-up__video", { keepMuted: true });
  }

  shadeCount() {
    return this.cart.data.lines
      .filter((l) => l.type === "default")
      .reduce((acc, l) => acc + l.quantity, 0);
  }

  // User tapped the row.
  onToggle() {
    if (this.earned) {
      // Locked in as free — keep it in the cart, ignore the toggle.
      this.input.checked = true;
      return;
    }
    this.userAdded = this.input.checked;
    if (this.input.checked) this.addItem();
    else this.removeItem();
    this.setState();
  }

  // Recompute earned from the live shade count; reconcile cart membership.
  syncEarned() {
    this.earned = this.shadeCount() >= this.triggerQuantity;
    const shouldBeIn = this.earned || this.userAdded;
    if (this.input.checked !== shouldBeIn) {
      this.input.checked = shouldBeIn;
      if (shouldBeIn) this.addItem();
      else this.removeItem();
    }
    this.setState();
  }

  setState() {
    const state = this.earned ? "earned" : this.userAdded ? "added" : "default";
    this.setAttribute("data-state", state);
    if (state === "earned") this.startTwkDrift();
    else this.stopTwkDrift();
  }

  // Drift the four "Free included" stars to fresh random points within a small
  // radius on a loop (CSS runs the per-star scale/rotate/fade). Earned state only;
  // no-op under reduced motion (CSS shows them static). Mirrors the demo's twkDrift.
  startTwkDrift() {
    if (this._twkTimer) return;
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const wrap = this.querySelector(".lp-up__twk");
    if (!wrap) return;
    const stars = Array.from(wrap.querySelectorAll("i"));
    if (!stars.length) return;
    const R = 18;
    const place = (el) => {
      const t = Math.random() * Math.PI * 2;
      const rr = R * Math.sqrt(Math.random());
      el.style.setProperty("--tx", (Math.cos(t) * rr).toFixed(1) + "px");
      el.style.setProperty("--ty", (Math.sin(t) * rr).toFixed(1) + "px");
    };
    stars.forEach((el, i) => {
      place(el);
      const b = el.querySelector("b");
      if (b) b.style.animationDelay = (i * 0.37).toFixed(2) + "s";
    });
    this._twkTimer = setInterval(() => {
      stars.forEach((el) => {
        if (Math.random() < 0.65) place(el);
      });
    }, 680);
  }

  stopTwkDrift() {
    if (this._twkTimer) {
      clearInterval(this._twkTimer);
      this._twkTimer = null;
    }
  }
}

// The orchestrator element: owns the virtual cart, checkout, and the footer paint;
// delegates the tracker / burst to the UI widgets above.
class LpCart extends HTMLElement {
  constructor() {
    super();
    this.data = {
      lines: [],
      localization: {
        country: { isoCode: this.getAttribute("country") || null },
      },
      currency: {
        format:
          this.getAttribute("money-format") ||
          "€{{amount_with_comma_separator}}",
        currencyCode: this.getAttribute("currency") || null,
      },
    };
  }

  connectedCallback() {
    this.basePrice = this.getAttribute("base_price") || "price";
    // Dev aid: add a `debug` attribute to <lp-cart> to log the discount config.
    this.debug = this.hasAttribute("debug");

    Shopify.cartQueue.enqueue({
      url: `${Shopify.routes.root}cart/clear.js`,
      options: { method: "POST" },
      callback: () => {},
      error: () => console.error("Cart clear failed!"),
    });

    this.tracker = new LpTracker(this);
    this.burst = new LpBurst(this);

    this.addEventListener("lp-cart:change", (event) => {
      if (!event?.detail?.cart) return;
      const cart = event.detail.cart;
      const { original, discounted } = getTotals(cart, this.basePrice);
      const cartCount = cart.lines.reduce((acc, l) => acc + l.quantity, 0);
      // Progress measures SELECTED products only — auto-added free gifts (type
      // 'upsell') must not count toward the milestone thresholds.
      const count = selectionCount(cart.lines);

      this.updateSection(original, discounted, cartCount, cart.currency.format);
      this.updateCardPrices(cart);
      this.updateUpsellPrices(cart);
      const adv = this.tracker.update(count, original, discounted);
      if (this.ready && this.burst.active && adv.advanced)
        this.burst.fire(adv.node, adv.idx + 1);
      this.selectionCount = count;
    });

    // Restore from bfcache cleanly (cart was cleared on load).
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) window.location.reload();
    });

    this.querySelectorAll("[checkout-button]").forEach((button) => {
      button.addEventListener("click", () => {
        if (!button.hasAttribute("disabled")) this.submit();
      });
    });

    const currency =
      (this.data.currency && this.data.currency.currencyCode) || "EUR";
    const milestones = buildMilestones(this, currency, this.debug);
    if (this.debug)
      console.log("[lp-cart] savings tracker milestones →", milestones);
    this.tracker.init(milestones, this.data.currency.format);
    this.observeFooterHeight();
    this.setupRange();

    // Arm celebrations only after the initial pre-selected cascade settles (items
    // dispatch lp-cart:change synchronously on upgrade; this timeout runs after).
    setTimeout(() => {
      this.ready = true;
      this.tracker.arm();
      this.captureCardHomes(); // snapshot the default layout/selection for reset
    }, 0);

    // The finder (a separate section) signals completion → reorganise into the result
    // layout (curated/secondary/explore); Start-over → restore the default. The reuse
    // seam: same cards, re-parented.
    document.addEventListener("lp-finder:complete", (event) =>
      this.applyFinderResult(event.detail),
    );
    document.addEventListener("lp-finder:scroll", () =>
      this.scrollToFinderResult(),
    );
    document.addEventListener("lp-finder:reset", () =>
      this.restoreDefaultLayout(),
    );
  }

  // Snapshot each shade card's home (parent, position, selected, description) so the
  // result layout can be undone exactly on Start over.
  captureCardHomes() {
    if (this.cardHomes) return; // capture the DEFAULT state once
    this.cardHomes = Array.from(this.querySelectorAll("lp-bundle-item")).map(
      (el) => {
        const input = el.querySelector("input");
        const desc = el.querySelector(".lp-card__desc");
        return {
          el: el,
          parent: el.parentNode,
          checked: input ? input.checked : false,
          desc: desc ? desc.innerHTML : null,
        };
      },
    );
  }

  // Paint the checkout footer totals + enable/disable the button.
  updateSection(originalTotal, discountedTotal, cartCount, format) {
    const savedAmount = Math.max(originalTotal - discountedTotal, 0);
    const savedPercent =
      discountedTotal > 0 ? Math.round((savedAmount * 100) / originalTotal) : 0;
    const originalFmt = formatMoney(originalTotal, format);
    const discountedFmt = formatMoney(discountedTotal, format);
    const savedFmt = formatMoney(savedAmount, format);

    if (cartCount < 1) {
      this.setAttribute("disable-checkout", "");
      this.querySelectorAll("[checkout-button]").forEach((b) =>
        b.setAttribute("disabled", ""),
      );
      this.querySelectorAll("[data-only-discount]").forEach((c) =>
        c.classList.add("hidden"),
      );
      return;
    }

    this.removeAttribute("disable-checkout");
    this.querySelectorAll("[checkout-button]").forEach((b) =>
      b.removeAttribute("disabled"),
    );
    const discounted = originalTotal > discountedTotal;
    this.querySelectorAll("[data-original-total]").forEach((c) => {
      c.innerHTML = originalFmt;
      c.classList.toggle("hidden", !discounted);
    });
    this.querySelectorAll("[data-discounted-total]").forEach(
      (c) => (c.innerHTML = discountedFmt),
    );
    this.querySelectorAll("[data-only-discount]").forEach((c) =>
      c.classList.toggle("hidden", !discounted),
    );
    this.querySelectorAll("[data-item-count]").forEach(
      (c) => (c.innerHTML = cartCount),
    );
    this.querySelectorAll("[data-saved-amount]").forEach(
      (c) => (c.innerHTML = savedFmt),
    );
    this.querySelectorAll("[data-saved-percent]").forEach(
      (c) => (c.innerHTML = savedPercent + "%"),
    );
  }

  // Per-card price under the live bundle discount. The Hy app only gives a whole-
  // cart total, so the discount is allocated to each SELECTED card by ratio
  // (discounted/original) — for a flat % bundle that's the same % on every card.
  //   now = price × ratio     was = compare-at (if any) else single price (struck)
  // Unselected cards (or no active discount) show the full price, no strike. The
  // footer total stays authoritative; per-card values are rounded for display.
  //
  // IMPORTANT: the ratio is computed over the SHADE lines only (type 'default').
  // The whole-cart total folds in auto-added free gifts (100% off → €0) and paid
  // add-ons; using that ratio would smear their savings onto the shade cards,
  // overstating each shade's discount. A shades-only cart isolates the bundle
  // discount that actually applies to the shades (and the scale tier still keys
  // off shade quantity, which is unchanged by dropping the other lines).
  updateCardPrices(cart) {
    const shadeCart = {
      ...cart,
      lines: cart.lines.filter((l) => l.type === "default"),
    };
    const { original: shadeOriginal, discounted: shadeDiscounted } = getTotals(
      shadeCart,
      this.basePrice,
    );
    const ratio = shadeOriginal > 0 ? shadeDiscounted / shadeOriginal : 1;
    const hasDiscount = ratio < 0.999;
    const format =
      (cart.currency && cart.currency.format) || this.data.currency.format;
    const selectedIds = new Set(
      cart.lines.filter((l) => l.type === "default").map((l) => l.variantId),
    );

    this.querySelectorAll("lp-bundle-item").forEach((item) => {
      const nowEl = item.querySelector(".lp-card__now");
      const wasEl = item.querySelector(".lp-card__was");
      if (!nowEl) return;
      const price = Number(item.getAttribute("variant-price")) || 0;
      const compareAt = item.hasAttribute("variant-compare-at-price")
        ? Number(item.getAttribute("variant-compare-at-price"))
        : 0;
      const selected = selectedIds.has(Number(item.getAttribute("variant-id")));

      if (selected && hasDiscount) {
        // Discounted: now = proportional bundle price; was = compare-at or price.
        nowEl.innerHTML = formatMoney(price * ratio * 100, format);
        if (wasEl) {
          const wasVal = compareAt > price ? compareAt : price;
          wasEl.innerHTML = formatMoney(wasVal * 100, format);
          wasEl.hidden = false;
        }
      } else {
        // Full price; keep the static compare-at strike if the product has one.
        nowEl.innerHTML = formatMoney(price * 100, format);
        if (wasEl) {
          if (compareAt > price) {
            wasEl.innerHTML = formatMoney(compareAt * 100, format);
            wasEl.hidden = false;
          } else {
            wasEl.hidden = true;
          }
        }
      }
    });
  }

  // Effective per-unit price (major currency units) of an upsell under the live cart.
  // The Hy app discounts cart lines but exposes only whole-cart totals, so derive the
  // line's price by diffing the discounted total WITH vs WITHOUT this line — no rule is
  // hardcoded here, so "2nd add-on 50% off at 3 shades" (or any config) is honoured as
  // data. When the upsell isn't in the cart we simulate adding one unit, so the tile can
  // advertise the discount before it's selected. Falls back to full price (no Hy app, or
  // no real saving). Skips earned gifts — their free/struck pill is handled by CSS.
  discountedUnitPrice(el) {
    const full = Number(el.getAttribute("variant-price")) || 0;
    const api = lpDiscountApi();
    if (!api || !el.data) return full;
    return lineDiscountedPrice({
      cart: this.data,
      line: el.data,
      full,
      calcDiscounted: (c) => api.calculateDiscountedTotal(c),
    });
  }

  // Reflect any live per-line discount on the upsell tiles' price pill: struck full
  // price (.lp-up__was) + discounted "now" (.lp-up__now), toggled via .lp-up--discounted.
  // The discounted unit price is stamped on the element (_lpUnitPrice) so the footer's
  // companion pill (LpTracker.updateCompanions) reads the same figure. Earned (free-gift)
  // tiles keep their own free/struck treatment, so we don't touch their pill.
  updateUpsellPrices(cart) {
    const format =
      (cart.currency && cart.currency.format) || this.data.currency.format;
    // Earned status from the LIVE selection count, NOT the tile's data-state: that
    // attribute is set by each upsell's own lp-cart:change listener, which is registered
    // after LpCart's (the parent upgrades first) and so runs AFTER this. Reading it here
    // is a step stale — e.g. dropping 4→3 left the pill on the earned/full price instead
    // of reverting to the discount. cart.lines is already current when this fires.
    const count = selectionCount(cart.lines);
    this.querySelectorAll("lp-upsell-item").forEach((el) => {
      const full = Number(el.getAttribute("variant-price")) || 0;
      const trigger = Number(el.getAttribute("trigger-quantity")) || 0;
      const earned = isEarned(count, trigger);
      const unit = earned ? full : this.discountedUnitPrice(el);
      el._lpUnitPrice = unit;
      const hasDiscount = !earned && unit < full - 0.0001;
      el.classList.toggle("lp-up--discounted", hasDiscount);
      if (earned) return; // earned pill (struck full + "Free") is CSS-driven
      const nowEl = el.querySelector(".lp-up__now");
      const wasEl = el.querySelector(".lp-up__was");
      if (nowEl)
        nowEl.innerHTML = formatMoney(
          (hasDiscount ? unit : full) * 100,
          format,
        );
      if (wasEl && hasDiscount)
        wasEl.innerHTML = formatMoney(full * 100, format);
    });
  }

  // "Explore the rest of the range" disclosure: toggle the panel + aria-expanded,
  // and swap the heading between its closed/open labels.
  setupRange() {
    const toggle = this.querySelector("[data-lp-range-toggle]");
    const panel = this.querySelector("[data-lp-range-panel]");
    if (!toggle || !panel) return;
    const label = toggle.querySelector("[data-lp-range-label]");
    // Labels are inline_richtext (escaped in the attribute, decoded by getAttribute),
    // so they may carry an <em> accent — swap via innerHTML, not textContent, to keep it.
    const closed = toggle.getAttribute("data-label-closed") || "";
    const open = toggle.getAttribute("data-label-open") || closed;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    toggle.addEventListener("click", () => {
      const next = toggle.getAttribute("aria-expanded") !== "true";
      toggle.setAttribute("aria-expanded", String(next));
      panel.classList.toggle("is-open", next);
      if (!label) return;
      const text = next ? open : closed;
      if (reduce.matches) {
        label.innerHTML = text;
        return;
      }
      // Fade out, swap at the midpoint, fade back in — overlaps the panel motion.
      // Keep this delay equal to the .lp-range__heading opacity transition.
      label.classList.add("is-swapping");
      window.setTimeout(() => {
        label.innerHTML = text;
        label.classList.remove("is-swapping");
      }, 150);
    });
    this.updateRangeCount();
  }

  // Fill {count} in the "Explore the rest" sub-line with the accordion's card count
  // (the additional group by default; the non-recommended set after the finder runs).
  updateRangeCount() {
    const sub = this.querySelector(".lp-range__sub");
    if (!sub) return;
    if (this.rangeSubTpl == null) this.rangeSubTpl = sub.textContent;
    if (!/\{count\}/.test(this.rangeSubTpl)) return;
    const grid = this.querySelector("[data-lp-range-panel] .lp-range__grid");
    const count = grid ? grid.querySelectorAll(".lp-card").length : 0;
    sub.textContent = this.rangeSubTpl.replace(/\{count\}/g, count);
  }

  multiATC() {
    const payload = {
      items: this.data.lines.map(({ variantId, quantity }) => ({
        id: variantId,
        quantity,
        properties: { _lp_exclusive: "true" },
      })),
    };
    Shopify.cartQueue.enqueue({
      url: `${Shopify.routes.root}cart/add.js`,
      options: {
        body: JSON.stringify(payload),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
      callback: () => {
        // Write the finder attributes (if any), then go to checkout.
        this.persistFinderAttributes(() => {
          setTimeout(() => {
            this.setLoading(false);
            window.location.href = `${Shopify.routes.root}checkout`;
          }, payload.items.length * 100);
        });
      },
      error: () => {
        this.setLoading(false);
        console.error("Cart add failed!");
      },
    });
  }

  // Persist the finder run as hidden cart attributes (→ order) before checkout,
  // namespaced by finder id (e.g. "lp_shade"):
  //   _<id>_traits      — the customer's answers as JSON
  //   _<id>_recommended — the recommended variant ids as CSV
  // Never blocks checkout if the update fails. `next` continues the flow.
  persistFinderAttributes(next) {
    const id = this.finderId || "lp_shade";
    const attributes = {};
    // Always record the landing page (page handle — last path segment, query stripped),
    // regardless of whether the quiz was completed.
    const landingPage =
      window.location.pathname.split("/").filter(Boolean).pop() || "";
    if (landingPage) {
      attributes["_lp_landing_page"] = "true";
    }
    // Flat, Flow-friendly hidden attributes (no JSON, since Shopify Flow handles it poorly):
    // one per trait (keys match the canonical trait.* metafields), the quiz id, and the
    // recommended variant ids as a CSV. Each maps 1:1 to an order/customer metafield in Flow.
    const traits = this.finderTraits || {};
    const hasTraits = Object.keys(traits).length > 0;
    const hasRecommended =
      this.finderRecommended && this.finderRecommended.length;
    if (hasTraits || hasRecommended) {
      attributes["_" + id + "_quiz"] = id;
    }
    if (hasTraits) {
      Object.keys(traits).forEach((axis) => {
        attributes["_" + id + "_" + axis] = traits[axis];
      });
    }
    if (hasRecommended) {
      attributes["_" + id + "_recommended"] = this.finderRecommended.join(",");
    }
    if (!Object.keys(attributes).length) {
      next();
      return;
    }
    Shopify.cartQueue.enqueue({
      url: `${Shopify.routes.root}cart/update.js`,
      options: {
        body: JSON.stringify({ attributes }),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
      callback: () => next(),
      error: () => {
        console.error("Finder cart attributes update failed");
        next();
      },
    });
  }

  // Submitting state: dim + spinner via the `loading` class, and aria-busy on the
  // checkout button(s) for screen readers.
  setLoading(on) {
    this.classList.toggle("loading", on);
    this.querySelectorAll("[checkout-button]").forEach((b) => {
      if (on) b.setAttribute("aria-busy", "true");
      else b.removeAttribute("aria-busy");
    });
  }

  submit() {
    this.setLoading(true);
    Shopify.cartQueue.enqueue({
      url: `${Shopify.routes.root}cart/clear.js`,
      options: { method: "POST" },
      callback: () => {},
      error: () => console.error("Cart clear failed!"),
    });
    this.multiATC();
  }

  // Keep --footer-h in sync with the real footer height, and reserve that height at the
  // page bottom (on <body>) so the last section clears the fixed checkout bar — wherever
  // it falls in the section order (e.g. lp-about renders after this section).
  observeFooterHeight() {
    const footer = this.querySelector(".lp-products__footer");
    if (!footer) return;
    const set = () => {
      const h = footer.offsetHeight;
      this.style.setProperty("--footer-h", h + "px");
      document.body.style.paddingBottom = h + "px";
    };
    set();
    if ("ResizeObserver" in window) {
      this.footerObserver = new ResizeObserver(set);
      this.footerObserver.observe(footer);
    } else {
      window.addEventListener("resize", set);
    }
  }

  // The finder passes the recommended variant ids; set the selection to exactly
  // those (select picks, deselect the rest), then scroll the products into view.
  applyFinderResult(detail) {
    const recommended = (detail && detail.recommended) || [];
    // Remember the run for the hidden cart attributes (→ order → Flow). finderId
    // namespaces the keys; finderRecommended is the full ordered match set.
    this.finderId = (detail && detail.finderId) || "lp_shade";
    this.finderTraits = (detail && detail.answers) || null;
    const recIds = recommended.map((r) => Number(r.variantId)).filter(Boolean);
    this.finderRecommended = recIds;
    if (!recIds.length) return;

    this.captureCardHomes(); // safety: ensure the default snapshot exists

    // Per-shade copy keyed by variant id (from the outcomes JSON).
    const textById = {};
    recommended.forEach((r) => {
      if (r.text) textById[Number(r.variantId)] = r.text;
    });

    const curatedN = Number(this.getAttribute("data-curated-count")) || 4;
    const selectedN = Number(this.getAttribute("data-selected-count")) || 3;

    // The whole baseline / "as advertised" feature is gated on the merchant having set
    // an "As advertised" heading (data-advertised="1"). Blank heading → feature dormant:
    // no card moves, no tertiary pill, no forced re-selection — plain finder output.
    // (Lets the code ship to the live page before the copy/config is in place.)
    const advertisedOn = this.getAttribute("data-advertised") === "1";

    // Variant ids that were pre-selected on page load (the merchant's baseline set,
    // captured before any interaction). These get moved to the advertised area (or keep
    // the tertiary pill if already a top match) and are re-selected even if the customer
    // had deselected them. Only valid (enabled) candidates qualify.
    const preselectedIds = !advertisedOn
      ? []
      : (this.cardHomes || [])
          .filter((h) => {
            if (!h.checked) return false;
            const input = h.el.querySelector("input");
            return input && !input.disabled;
          })
          .map((h) => Number(h.el.getAttribute("variant-id")))
          .filter(Boolean);

    const preselectedSet = new Set(preselectedIds);

    // Finder's natural curated top-N (baseline defaults are NOT forced in here).
    const curatedIds = recIds.slice(0, curatedN);
    const curatedSet = new Set(curatedIds);
    // Baseline ("as advertised") products NOT in the curated top-N → moved into the
    // dedicated area above the match section. Ones already in the top-N stay put and
    // just gain the tertiary pill (handled in layoutFinderResult via preselectedSet).
    const advertisedIds = preselectedIds.filter((id) => !curatedSet.has(id));
    const advertisedSet = new Set(advertisedIds);
    // Secondary = finder recs beyond the top-N, minus any pulled into the advertised area.
    const secondaryIds = recIds
      .slice(curatedN)
      .filter((id) => !advertisedSet.has(id));
    const recSet = new Set([...curatedIds, ...secondaryIds, ...advertisedIds]);

    // 1. Clear every current selection (gifts re-derive from the new shade count).
    this.querySelectorAll("lp-bundle-item input:checked").forEach((input) => {
      if (input.disabled) return;
      input.checked = false;
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });

    // 2. Re-parent into advertised / curated / secondary / explore + copy + fit pills.
    this.layoutFinderResult(
      curatedIds,
      secondaryIds,
      advertisedIds,
      recSet,
      textById,
      preselectedSet,
    );
    this.updateRangeCount(); // "Explore the rest" now holds the non-recommended count

    // 3. Choice pills (the customer's answers).
    this.renderChoicePills((detail && detail.answerDetails) || []);

    // 4. Pre-select the first N of the curated stack, plus the baseline defaults
    //    (always re-selected, even if the customer had deselected them earlier).
    const selectIds = new Set(curatedIds.slice(0, selectedN));
    preselectedIds.forEach((id) => selectIds.add(id));
    this.querySelectorAll("lp-bundle-item").forEach((item) => {
      const input = item.querySelector("input");
      if (!input || input.disabled) return;
      const want = selectIds.has(Number(item.getAttribute("variant-id")));
      if (input.checked !== want) {
        input.checked = want;
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    });

    // 5. Enter result mode. The layout is applied here (off-screen, the moment the
    //    finder's result screen appears); the scroll + focus happen later, only when
    //    the customer taps the CTA (lp-finder:scroll → scrollToFinderResult).
    this.classList.add("is-finder-result");
  }

  // Scroll the curated panel into view and move focus to its heading (announces the
  // result to screen readers). Triggered by the finder CTA, not by applying the
  // result — the grid is already laid out by the time this runs.
  scrollToFinderResult() {
    if (!this.classList.contains("is-finder-result")) return;
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const panel = this.querySelector("[data-result-a]") || this;
    const heading =
      this.querySelector("[data-result-curated-heading]") || panel;
    heading.setAttribute("tabindex", "-1");
    heading.focus({ preventScroll: true });
    // Defer to the next frame so any pending reflow settles before we measure. A
    // same-tick smooth scroll can target a pre-reflow position and overshoot —
    // cutting off the top — especially on iOS Safari, whose collapsing toolbar
    // shifts the layout mid-scroll. Land a little below the panel top.
    requestAnimationFrame(() => {
      const offset =
        window.pageYOffset || document.documentElement.scrollTop || 0;
      const top = panel.getBoundingClientRect().top + offset - 20;
      const max = Math.max(
        0,
        (document.documentElement.scrollHeight || 0) - window.innerHeight,
      );
      window.scrollTo({
        top: Math.max(0, Math.min(top, max)),
        behavior: reduce ? "auto" : "smooth",
      });
    });
  }

  // Move cards into curated/secondary grids + the explore accordion; set per-shade copy
  // (both layouts have a desc slot) and the fit pill.
  layoutFinderResult(
    curatedIds,
    secondaryIds,
    advertisedIds,
    recSet,
    textById,
    preselectedSet,
  ) {
    const curatedGrid = this.querySelector("[data-curated-grid]");
    const secondaryGrid = this.querySelector("[data-secondary-grid]");
    const advertisedGrid = this.querySelector("[data-advertised-grid]");
    const explore = this.querySelector("[data-lp-range-panel] .lp-range__grid");
    const fitStrong = this.getAttribute("data-fit-strong") || "";
    const fitSoft = this.getAttribute("data-fit-soft") || "";
    const preselected = preselectedSet || new Set();
    const byId = {};
    this.querySelectorAll("lp-bundle-item").forEach((el) => {
      byId[Number(el.getAttribute("variant-id"))] = el;
    });

    curatedIds.forEach((id) => {
      const el = byId[id];
      if (!el || !curatedGrid) return;
      curatedGrid.appendChild(el);
      this.setCardText(el, textById[id]);
      this.setCardFit(el, "strong", fitStrong);
      // A baseline product that's also a top match keeps the curated pill AND the tertiary.
      this.setCardFit3(el, preselected.has(id));
    });
    secondaryIds.forEach((id) => {
      const el = byId[id];
      if (!el || !secondaryGrid) return;
      secondaryGrid.appendChild(el);
      this.setCardText(el, textById[id]);
      this.setCardFit(el, "soft", fitSoft);
      this.setCardFit3(el, false);
    });
    // Baseline ("as advertised") products not in the top-N: their own area, tertiary
    // pill only (no curated/secondary fit pill).
    (advertisedIds || []).forEach((id) => {
      const el = byId[id];
      if (!el || !advertisedGrid) return;
      advertisedGrid.appendChild(el);
      this.setCardText(el, textById[id]);
      this.setCardFit(el, null);
      this.setCardFit3(el, true);
    });
    // Everything else → the "Explore the rest" accordion.
    Object.keys(byId).forEach((idStr) => {
      const id = Number(idStr);
      if (recSet.has(id)) return;
      this.setCardFit(byId[id], null);
      this.setCardFit3(byId[id], false);
      if (explore) explore.appendChild(byId[id]);
    });

    const secWrap = this.querySelector("[data-secondary]");
    if (secWrap) secWrap.style.display = secondaryIds.length ? "" : "none";
    const advWrap = this.querySelector("[data-result-advertised]");
    if (advWrap) advWrap.hidden = (advertisedIds || []).length === 0;
  }

  setCardText(el, text) {
    if (!text) return;
    const d = el.querySelector(".lp-card__desc"); // present in both layouts
    if (d) d.textContent = text;
  }

  setCardFit(el, kind, label) {
    const fit = el.querySelector("[data-card-fit]");
    if (!fit) return;
    fit.classList.remove("lp-card__fit--strong", "lp-card__fit--soft");
    if (!kind) {
      fit.hidden = true;
      return;
    }
    fit.classList.add("lp-card__fit--" + kind);
    const lbl = fit.querySelector("[data-card-fit-label]");
    if (lbl) lbl.textContent = label || "";
    fit.hidden = false;
  }

  // Tertiary "as advertised" pill — shown on baseline (pre-selected) products. Label is
  // the merchant-set data-fit-third; colours come from CSS vars on the host.
  setCardFit3(el, on) {
    const pill = el.querySelector("[data-card-fit3]");
    if (!pill) return;
    if (!on) {
      pill.hidden = true;
      return;
    }
    pill.textContent = this.getAttribute("data-fit-third") || "";
    pill.hidden = false;
  }

  renderChoicePills(details) {
    const wrap = this.querySelector("[data-result-choices]");
    if (!wrap) return;
    const esc = (s) =>
      String(s == null ? "" : s).replace(
        /[&<>"]/g,
        (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c],
      );
    wrap.innerHTML = details
      .map(
        (a) =>
          '<span class="lp-products__choice">' +
          '<span class="lp-products__choice-sw" style="background:' +
          esc(a.swatch) +
          '"></span>' +
          '<span class="lp-products__choice-tx">' +
          '<span class="lp-products__choice-val">' +
          esc(a.label) +
          "</span>" +
          (a.dim
            ? '<span class="lp-products__choice-dim">' + esc(a.dim) + "</span>"
            : "") +
          "</span></span>",
      )
      .join("");
  }

  // Start over: cards back to their home parents (capture order), description + default
  // selection restored, pills/fit cleared, result mode dropped.
  restoreDefaultLayout() {
    if (!this.classList.contains("is-finder-result")) return;
    if (this.cardHomes) {
      this.cardHomes.forEach((h) => {
        if (h.parent) h.parent.appendChild(h.el);
        if (h.desc != null) {
          const d = h.el.querySelector(".lp-card__desc");
          if (d) d.innerHTML = h.desc;
        }
        this.setCardFit(h.el, null);
        this.setCardFit3(h.el, false);
      });
      this.cardHomes.forEach((h) => {
        const input = h.el.querySelector("input");
        if (input && !input.disabled && input.checked !== h.checked) {
          input.checked = h.checked;
          input.dispatchEvent(new Event("input", { bubbles: true }));
        }
      });
    }
    const choices = this.querySelector("[data-result-choices]");
    if (choices) choices.innerHTML = "";
    const secWrap = this.querySelector("[data-secondary]");
    if (secWrap) secWrap.style.display = "";
    const advWrap = this.querySelector("[data-result-advertised]");
    if (advWrap) advWrap.hidden = true;
    this.classList.remove("is-finder-result");
    this.updateRangeCount(); // accordion back to the default additional count
  }
}

// Define lp-cart first so it's listening before the items upgrade and dispatch
// their initial (pre-selected) lp-cart:change events.
if (!customElements.get("lp-cart")) customElements.define("lp-cart", LpCart);
if (!customElements.get("lp-bundle-item"))
  customElements.define("lp-bundle-item", LpBundleItem);
if (!customElements.get("lp-upsell-item"))
  customElements.define("lp-upsell-item", LpUpsellItem);
